import { computed, inject } from '@angular/core';
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { AuthService } from '../services/auth.service';
import { Session } from '../models/data.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';
import { MediaPermissionsErrorType } from 'mic-check';

type AppState = {
  loading: boolean;
  ready: boolean;
  darkMode: boolean;
  session: Session;
  mediaPermissionsError: MediaPermissionsErrorType | null;
  // permissionMessage: string;
};

const initialState: AppState = {
  loading: false,
  ready: false,
  darkMode: false,
  session: {
    id: ''
  },
  mediaPermissionsError: MediaPermissionsErrorType.Generic,
  // permissionMessage: 'Checking camera permissions...'
};

export const AppStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store, authService = inject(AuthService)) => ({
    user: computed(() => authService.user()),
  })),
  withMethods((
    store,
    router = inject(Router),
    authService = inject(AuthService),
    snackbar = inject(MatSnackBar)
  ) => ({
    startLoading: () => {
      console.log('start loading');
      patchState(store, { loading: true });
    },
    stopLoading: () => {
      console.log('stop loading');
      patchState(store, { loading: false });
    },
    setMediaPermissionsError: (error: MediaPermissionsErrorType | null) => {
      patchState(store, { mediaPermissionsError: error });
    },
    // setPermissionsMessage: (message: string) => {
    //   patchState(store, { permissionMessage: message });
    // },
    setSession: (session: Session) => {
      patchState(store, { session: {...session} });
    },
    setReady: () => {
      patchState(store, { ready: true });
    },
    setDarkMode: (darkMode: boolean) => {
      patchState(store, { darkMode });
      document.documentElement.classList.toggle('dark', darkMode);
    },
    login: async (email: string, password: string) => {
      if (!email || !password) {
        return;
      }

      try {
        patchState(store, { loading: true });
        await authService.login(email, password);
        router.navigate(['/dashboard']);
      } catch (error) {
        snackbar.open('Invalid email or password', 'Close', {
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      } finally {
        patchState(store, { loading: false });
      }
    },
    logout: async () => {
      await authService.logout();
      router.navigate(['/login']);
    },
    resetPassword: async (email: string) => {
      if (!email) {
        return;
      }

      try {
        patchState(store, { loading: true });
        await authService.resetPassword(email);
        snackbar.open('Password reset email sent', 'Close', {
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      } catch (error) {
        snackbar.open('Invalid email', 'Close', {
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      } finally {
        patchState(store, { loading: false });
      }
    },
    confirmPasswordReset: async (code: string, newPassword: string) => {
      if (!code || !newPassword) {
        return;
      }

      try {
        patchState(store, { loading: true });
        await authService.confirmPasswordReset(code, newPassword);
        snackbar.open('Password reset successful', 'Close', {
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      } catch (error) {
        snackbar.open('Invalid code or password', 'Close', {
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      } finally {
        patchState(store, { loading: false });
      }
    },
    loaderOnNavigation: rxMethod<any>(
      pipe(
        tap((event) => {
          if (event instanceof NavigationStart) {
            patchState(store, { loading: true });
          } else if (
            event instanceof NavigationEnd ||
            event instanceof NavigationError
          ) {
            patchState(store, { loading: false });
          }
        })
      )
    ),
  })),
  withHooks((store, router = inject(Router)) => ({
    onInit() {
      store.loaderOnNavigation(router.events);
    },
  }))
);
